import produce from 'immer';
import {compact, each, findIndex, isArray, keyBy, set, toNumber} from 'lodash';
import {packId} from 'common/diplomatIdUtil';
import {DiplomatFolder, IDiplomatFolder} from 'types/DiplomatFolder';
import {IDiplomatObject} from 'types/DiplomatObject';
import {ensureFirstLevelOrder} from './ensureFirstLevelOrder';
import {DiplomatObjectType} from 'types/DiplomatObjectType';
import {ALL_USERS, SFTP_GROUPS} from 'common/constants';
import {alphaNumericSortBy, baseNameComparator} from 'common/stringUtils';

export type INav = {
   // NOTE: id combined objectType/Id for all types except folders
   child?: INav[];
   count?: number;
} & IDiplomatFolder;

export type INavState = {
   _state: {
      depth: number;
      expanded?: boolean;
   };
} & INav;

export const combineSFTP = (folders: IDiplomatFolder[]) => {
   const root = DiplomatFolder.cast({
      id: '-99',
      name: 'File Transfer Server',
      level: 1,
      objectType: DiplomatObjectType.enum.FOLDER,
      childObjectType: DiplomatObjectType.enum.SFTP_SERVER,
      immutable: true,
   });

   folders.forEach((f) => {
      if (f.level === 0) {
         root.folderId = f.id;
      }
      if (
         findIndex(
            [
               DiplomatObjectType.enum.VFS,
               DiplomatObjectType.enum.VFS_GROUP,
               DiplomatObjectType.enum.SFTP_USER,
            ],
            (type) => type === f.childObjectType
         ) >= 0 &&
         f.level === 1
      ) {
         f.folderId = root.id;
         f.level = +f.level + 1;

         if (f.childObjectType === DiplomatObjectType.enum.SFTP_USER) {
            f.comparisonText = '1';
         } else if (f.childObjectType === DiplomatObjectType.enum.VFS_GROUP) {
            f.comparisonText = '2';
         } else {
            f.comparisonText = '3';
         }

         console.debug('found sftp', f);
      }
   });
   folders.push(root);
};

/**
 * fill folderId of 'ALL Users', because it cames without it
 */
export const handleVfsGroup = (groups: IDiplomatObject[], folders: IDiplomatFolder[]) => {
   const allUsersG = groups.find((g) => g.name.toLocaleLowerCase() === ALL_USERS);
   const rootVfsGroupFolder = folders.find((f) => f.name === SFTP_GROUPS);
   // console.debug('handleVFSGroup', allUsersG, rootVfsGroupFolder);
   if (allUsersG && rootVfsGroupFolder && !allUsersG.folderId) {
      allUsersG.folderId = rootVfsGroupFolder.id;
   }
};

class FoldersBuilder {
   private mapById: any = {};

   private pushToParent = (item: INav) => {
      const parentFolder = this.mapById[item.folderId as string];
      if (!parentFolder) {
         // TODO host key is receiving without folderId...
         console.warn('Parent folder is not found for item', item.id);
         return false;
      } else {
         parentFolder.child = parentFolder.child || [];
         parentFolder.child.push(item);
         return true;
      }
   };

   private populateBy = (item?: any) => {
      if (typeof item?.folderId === 'string') {
         const _item = produce(item as INav, (draft) => {
            // draft.imageUrl = iconUrl;
            draft._id = draft.id;
            draft.id = packId(draft.objectType, draft.id);
         });

         this.pushToParent(_item);
         this.mapById[_item.id] = item;
      } else {
         each(item, (i) => this.populateBy(i));
      }
   };

   buildFolders = (
      rootName: string,
      folders: INav[],
      objects?: IDiplomatObject[],
      excludeEmpty?: boolean
   ) => {
      const _sorted = ensureFirstLevelOrder(
         alphaNumericSortBy(folders, ['level', 'comparisonText', 'name'])
      );

      const sortedFolders = produce(_sorted, (sorted) => {
         this.mapById = keyBy(sorted, 'id');

         // root of the folders
         let root: INav = sorted[0];
         if (rootName) {
            root.name = rootName;
         }

         // iterate other each folder and fill with nested folders
         sorted.forEach((folder: INav) => {
            if (toNumber(folder.level) === 0) {
               root = folder;
               return;
            }

            this.pushToParent(folder);
         });

         // fill with items inside main folders
         this.populateBy(objects?.sort(baseNameComparator));

         if (!root.child || !root.child.length) {
            console.warn('Root folder is empty');
         }

         this.countChildren(root);
         if (excludeEmpty) {
            this.excludeEmptyFolders(root);
         }
      });

      // store map of objects for future faster searches
      Object.assign(this.mapById, keyBy(_sorted, 'id'));

      // const root = find(sortedFolders, (folder: INav) => toNumber(folder.level) === 0);
      return [sortedFolders[0]];
   };

   private excludeEmptyFolders = (data: INav, i?: number) => {
      if (data.child && Number(data.count) > 0) {
         data.child = data.child.filter(this.excludeEmptyFolders);
         return true;
      }
      return data.objectType !== DiplomatObjectType.enum.FOLDER;
   };

   private countChildren = (data: INav): number => {
      if (data.child && data.child.length) {
         const count = data.child
            .map((item: any) => this.countChildren(item))
            .reduce((total: number, itemCount: number) => total + itemCount, 0);

         data.count = count;

         return count;
      } else if (!data.level) {
         return 1;
      }
      data.count = 0;
      return 0;
   };

   buildFoldersWithObjects = (folders: IDiplomatFolder[], objects?: IDiplomatObject[]) => {
      const _sorted = alphaNumericSortBy(folders, ['level', 'name']);
      // console.debug(_sorted);
      const sortedFolders = produce(_sorted as (IDiplomatFolder | null)[], (sorted) => {
         this.mapById = keyBy(sorted, 'id');

         // iterate other each folder and fill with nested folders
         sorted.forEach((folder: INav | null, i) => {
            if (this.pushToParent(folder!)) {
               sorted[i] = null;
            }
         });

         // fill with items inside main folders
         this.populateBy(sortedFolders);
      });

      // const root = find(sortedFolders, (folder: INav) => toNumber(folder.level) === 0);
      return compact(sortedFolders);
   };
}

export const foldersBuilder = new FoldersBuilder();
